import { Box, Button } from '@utilitywarehouse/hearth-react';
import { Formik } from 'formik';

import { DialogActions } from '@components/Dialog';
import { getEnergyTariffByType } from '@features/Energy/utils';
import {
  ChargeAmount,
  EnergyQuote,
  EnergyQuoteType,
} from '@generated/gql/graphql';
import { UpdatedTariffModalState } from '@providers/basket/types';
import { UpdatedTariffModalVariant } from '@src/types/bundle';

import { TariffFormValues } from '../types';
import { validateForm } from '../validate';
import { ConfettiBar } from './ConfettiBar';
import { Form } from './Form';

interface ContentProps {
  tariffs: EnergyQuote[];
  initialQuoteType: EnergyQuoteType;
  heading: string;
  modalState: UpdatedTariffModalState;
  selectedPrice?: Nullable<ChargeAmount>;
  hasConfetti: boolean;
  onSubmit: (quoteType: EnergyQuoteType) => Promise<void>;
  onBreakdownClick: (variant: EnergyQuoteType) => void;
  isPassiveClient: boolean;
}

const getInitialTariffType = (
  variant: UpdatedTariffModalVariant,
  fixedTariff?: Nullable<EnergyQuote>,
  trackerTariff?: Nullable<EnergyQuote>,
  initialChoice?: Nullable<EnergyQuote>
): EnergyQuoteType => {
  if (variant === UpdatedTariffModalVariant.productAdded) {
    return initialChoice?.electricity?.quoteType ?? EnergyQuoteType.Variable;
  }

  if ((fixedTariff?.isEligible || trackerTariff?.isEligible) && initialChoice) {
    return initialChoice.electricity?.quoteType ?? EnergyQuoteType.Variable;
  }

  return EnergyQuoteType.Variable;
};

const TariffForm = ({
  modalState,
  tariffs,
  initialQuoteType,
  heading,
  selectedPrice,
  hasConfetti,
  onSubmit,
  onBreakdownClick,
  isPassiveClient,
}: ContentProps) => {
  const initialChoice = getEnergyTariffByType(tariffs, initialQuoteType);
  const fixedTariff = getEnergyTariffByType(tariffs, EnergyQuoteType.Fixed);
  const trackerTariff = getEnergyTariffByType(tariffs, EnergyQuoteType.Tracker);

  const { variant } = modalState;

  const initialValues: TariffFormValues = {
    tariffType: getInitialTariffType(
      variant,
      fixedTariff,
      trackerTariff,
      initialChoice
    ),
  };

  const handleSubmit = async (values: TariffFormValues) => {
    onSubmit(values.tariffType);
  };

  return (
    <>
      {hasConfetti && <ConfettiBar />}
      <Formik
        initialValues={initialValues}
        validate={validateForm}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ handleSubmit, isValid }) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="energy-tariff-modal__form"
          >
            <Form
              tariffs={tariffs}
              heading={heading}
              initialQuoteType={initialQuoteType}
              modalState={modalState}
              selectedPrice={selectedPrice}
              onBreakdownClick={onBreakdownClick}
              isPassiveClient={isPassiveClient}
            />
            <Box className="energy-tariff-modal__form-actions">
              <DialogActions>
                <Button
                  disabled={!isValid}
                  data-testid="energy-tariff-modal-continue"
                  type="submit"
                  variant="solid"
                >
                  Continue
                </Button>
              </DialogActions>
            </Box>
          </form>
        )}
      </Formik>
    </>
  );
};

export { TariffForm };
